/**
 * Browser push for GPS-tracker alerts (the web twin of the Android app's push/ package).
 *
 * The backend sends one FCM message per person to every registered token — Android phones and
 * browsers alike (vTrackApp FcmPushGateway sets a WebpushConfig) — and the service worker
 * `public/yekkada-push-sw.js` turns its data map into a notification. Every anti-spam rule
 * (debounce, one entry per vehicle, quiet hours, ceiling, grouping, mutes) runs on the server.
 *
 * Off unless the build has the Firebase web config (REACT_APP_FIREBASE_API_KEY, _APP_ID,
 * _MESSAGING_SENDER_ID, _PROJECT_ID, _VAPID_KEY). The Firebase SDK is loaded lazily, only when a
 * person turns browser alerts on, so it costs nothing otherwise.
 *
 * Registration lifecycle mirrors Android: explicit logout unregisters (`disableWebPush`); a
 * session merely expiring does not, so alerts keep arriving after the 30-minute session ends.
 */
import axios from "axios";
import * as Constants from "./Constants";
import {getAuthToken} from "./utility";
import {isWebPushSupported, registrationBody, setVehicleEnabled, webPushConfig} from "./webPushCore";

export {isWebPushSupported, registrationBody, setVehicleEnabled, webPushConfig};

export const SW_URL = "/yekkada-push-sw.js";
// The token this browser registered. Lost with localStorage on any sign-out; refreshWebPush
// re-registers on the next start while the permission is still granted.
const TOKEN_KEY = "yk_web_push_token";

/** "unsupported" | "unconfigured" | "denied" | "default" | "granted". */
export function webPushState() {
  if (!isWebPushSupported()) return "unsupported";
  if (!webPushConfig()) return "unconfigured";
  return window.Notification.permission;
}

function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

function pushUrl(path) {
  return `${Constants.BASE_URL}${Constants.API_V1}/push/${path}`;
}

function authHeaders() {
  return {headers: {Authorization: `${Constants.BEARER} ${getAuthToken()}`}};
}

async function messagingAndRegistration() {
  const config = webPushConfig();
  const [{initializeApp, getApps}, {getMessaging, isSupported}] = await Promise.all([
    import("firebase/app"),
    import("firebase/messaging"),
  ]);
  if (!(await isSupported())) throw new Error("This browser does not support web push.");
  const app = getApps().find((a) => a.name === "yekkada-push") ||
    initializeApp(config.firebase, "yekkada-push");
  const registration = await navigator.serviceWorker.register(SW_URL);
  return {messaging: getMessaging(app), registration, vapidKey: config.vapidKey};
}

async function currentToken() {
  const {getToken} = await import("firebase/messaging");
  const {messaging, registration, vapidKey} = await messagingAndRegistration();
  return getToken(messaging, {vapidKey, serviceWorkerRegistration: registration});
}

async function registerToken(token) {
  const version = process.env.REACT_APP_VERSION || null;
  await axios.post(pushUrl("devices"), registrationBody(token, version, browserTimeZone()), authHeaders());
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * Turns browser alerts on for the signed-in person. Must run from a click (the browser's
 * permission prompt needs a user gesture). Resolves to the resulting state.
 */
export async function enableWebPush() {
  const state = webPushState();
  if (state === "unsupported" || state === "unconfigured") return state;
  const permission = await window.Notification.requestPermission();
  if (permission !== "granted") return permission;
  await registerToken(await currentToken());
  return "granted";
}

/**
 * Re-registers on app start when the person already allowed alerts here: picks up a rotated
 * token and re-binds it after a fresh login. Silent and best-effort.
 */
export async function refreshWebPush() {
  try {
    if (webPushState() !== "granted" || !getAuthToken()) return;
    await registerToken(await currentToken());
  } catch (e) {
    console.debug("[webPush] refresh skipped:", e?.message);
  }
}

/** Is this browser currently registered for the signed-in person? */
export function isWebPushOnHere() {
  return webPushState() === "granted" && !!localStorage.getItem(TOKEN_KEY);
}

/**
 * Stops alerts in this browser: tells the server (while the session is still valid), then
 * deletes the FCM token so it's dead even if that call fails. Call BEFORE clearing storage.
 */
export async function disableWebPush() {
  const token = localStorage.getItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_KEY);
  if (webPushState() !== "granted") return;
  if (token) {
    try {
      await axios.post(pushUrl("devices/remove"), {token}, authHeaders());
    } catch (e) {
      console.debug("[webPush] remove failed:", e?.message);
    }
  }
  // Even without the stored token (e.g. cleared by an earlier session expiry) deleting the
  // SDK's token kills it; the server drops it as unregistered on its next send.
  try {
    const {deleteToken} = await import("firebase/messaging");
    const {messaging} = await messagingAndRegistration();
    await deleteToken(messaging);
  } catch (e) {
    console.debug("[webPush] deleteToken failed:", e?.message);
  }
}
